import store from "@/store/index";
import router from "@/router";
import { ref, reactive, onBeforeMount, watch } from "@vue/composition-api";
import usePagination from "@/views/components/pagination/usePagination";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";
import { dynamicTableModel } from "@/models/data-management/dynamic-table";
import { bannerModel } from "@/models/banner/banner";
import useModal from "@/@core/components/base-modal/hooks/useModal";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";

export default function useBannerTable(props) {
  const { openModal, closeModal } = useModal();
  const {
    openSuccessModal,
    openDefaultErrorModal,
    openWarningModal,
  } = useValidationModal();

  const {
    entities,
    filters,
    ordering,
    searchValue,
    onSelectEntities,
    onSearch,
    sortingChanged,
    onSelectFilter,
  } = useFilterHeaderTable();

  const { fetchQueryDisplayModel, fetchQueryDataModel } = dynamicTableModel();

  const { fetchRequestBannerKeyModel, deleteBannerModel } = bannerModel();

  const setMode = ref(false);
  const isFilterReset = ref(false);
  const requestBannerKey = ref([]);
  
  const formModal = reactive({
    assignBanner: false
  })

  const loading = reactive({
    tableField: false,
    tableData: false,
    api: false,
  });

  const dynamicTable = reactive({
    column: [],
    payload: [],
    filterColumn: [],
    filter: [],
    isCreate: false,
  });

  const displayColumnOptions = reactive({
    options: null,
  });

  const assignBannerConfig = reactive({
    bannerId: null,
    option: {
      group: [],
      set: [],
    },
  });

  const modalPopup = reactive({
    report: false,
  });

  const onOpenExportModal = () => {
    modalPopup.report = true;
    openModal();
  };

  const closeReportModal = () => {
    modalPopup.report = false;
    closeModal();
  };

  const { pagination, paginationInfo } = usePagination();

  const actionList = reactive({
    edit: {
      label: "Edit",
      value: 1,
      icon: "EditIcon",
      display: true,
    },
    assignBannerGroup: {
      label: "Assign Banner Group",
      value: 2,
      icon: "AssignBannerGroupIcon",
      display: true,
    },
    AssignSetBanner: {
      label: "Assign Banner Set",
      value: 3,
      icon: "AssignSetBannerIcon",
      display: true,
    },
    delete: {
      label: "Delete",
      value: 4,
      icon: "DeleteIcon",
      display: true,
    },
  });

  const modeOptions = { set: 0, group: 1 };

  const resolveBannerStatusLabel = (status) => {
    switch (status) {
      case true:
        return "Active";
      case false:
        return "Inactive";

      default:
        return "Unknown";
    }
  };

  const fetchQueryDisplayList = () => {
    loading.tableData = true;
    fetchQueryDisplayModel.payload.DisplayId = props.queryId;
    fetchQueryDisplayModel.payload.Display_Key = props.displayKey;

    store
      .dispatch("api/apiRequest", fetchQueryDisplayModel)
      .then((response) => {
        displayColumnOptions.options = response.data.dynamic_fields;
        dynamicTable.column = orderBySequenceTable(
          selectDisplayTable(response.data.dynamic_fields)
        );

        fetchQueryDataList();
      })
      .catch((err) => {
        openDefaultErrorModal(err);
        loading.tableData = false;
      });
  };

  const fetchQueryDataList = (loadingAction = true) => {
    if (loadingAction) {
      loading.tableData = true;
    }

    fetchQueryDataModel.payload.Page = pagination.currentPage;
    fetchQueryDataModel.payload.Limit = entities.value || pagination.perPage;
    fetchQueryDataModel.payload.Ordering = ordering.value;
    fetchQueryDataModel.payload.Filters = filters.value;
    fetchQueryDataModel.payload.DisplayId = props.queryId;
    fetchQueryDataModel.payload.Display_Key = props.displayKey;
    fetchQueryDataModel.payload.QueryDataId = props.queryId;
    fetchQueryDataModel.payload.QueryData_Key = props.queryDataKey;
    fetchQueryDataModel.payload.IsVerified = true;

    store
      .dispatch("api/apiRequest", fetchQueryDataModel)
      .then((response) => {
        dynamicTable.payload = response.data.payload;
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const fetchRequestBannerKey = () =>
    new Promise((resolve, reject) => {
      store
        .dispatch("api/apiRequest", fetchRequestBannerKeyModel)
        .then((response) => {
          requestBannerKey.value = [...response.data.banner_dynamics_keys];
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });

  const deleteBanner = (bannerId) => {
    window.$loading(true);
    deleteBannerModel.payload.Banner_Id = bannerId;

    store
      .dispatch("api/apiRequest", deleteBannerModel)
      .then((response) => {
        openSuccessModal(
          "ทำรายการสำเร็จ !",
          null,
          "ตกลง",
          () => fetchQueryDataList(false),
          () => fetchQueryDataList(false)
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const selectDisplayTable = (fields) =>
    fields.filter((o) => o.is_display == true);

  const orderBySequenceTable = (fields) =>
    fields.sort((firstItem, secondItem) => firstItem.seq - secondItem.seq);

  const renderCell = (key) => `cell(${key})`;

  const renderHead = (key) => `head(${key})`;

  onBeforeMount(() => {
    fetchQueryDisplayList();
  });

  watch(
    [entities, filters, ordering],
    () => {
      pagination.currentPage = 1;

      if (!isFilterReset.value) {
        fetchQueryDataList();
      }

      isFilterReset.value = false;
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        fetchQueryDataList();
      }
    },
    { deep: true }
  );

  const resolveBannerAction = (actionType, itemData = null) => {
    switch (actionType) {
      case 1:
        if (itemData) {
          router.push(`/banner/list/Banner_List/${itemData.bannerid}/info`);
        }
        return;
      case 2:
        return openAssignModal(modeOptions.group, itemData);
      case 3:
        return openAssignModal(modeOptions.set, itemData);
      case 4:
        return openWarningModal(
          "ลบ แบนเนอร์",
          `ต้องการลบแบนเนอร์ ${itemData.name} หรือไม่`,
          () => {
            deleteBanner(itemData.bannerid);
          }
        );
      default:
    }
  };

  const resolveAlign = (textAlign) => {
    switch (textAlign) {
      case 1:
        return "left";
      case 2:
        return "center";
      case 3:
        return "right";
      default:
        return "right";
    }
  };

  const openAssignModal = async (mode, banner) => {
    try {
      formModal.assignBanner = true;
      if (requestBannerKey.value.length == 0) {
        await fetchRequestBannerKey();
      }

      const dynamicBannerId = requestBannerKey.value.find(
        (key) => key.static_key === "Banner_Id"
      );

      assignBannerConfig.bannerId = banner[dynamicBannerId.dynamic_key];

      switch (mode) {
        case modeOptions.set:
          setMode.value = true;
          return openModal();
        case modeOptions.group:
          setMode.value = false;
          return openModal();
        default:
          return;
      }
    } catch (err) {
      openDefaultErrorModal(err);
    }
  };

  const closeAssignModal = () => {
    setMode.value = false;
    formModal.assignBanner = false;
    closeModal();
  };

  const onFilterEntity = (numOfEntity) => {
    pagination.perPage = numOfEntity;
  };

  const onPageChange = (page) => {
    pagination.currentPage = page;
  };

  const onSearchBanner = (searchInput) => {
    searchValue.query = searchInput;
    onSearch();
  };

  return {
    loading,
    dynamicTable,
    pagination,
    paginationInfo,
    searchValue,
    displayColumnOptions,
    actionList,
    modeOptions,
    setMode,
    assignBannerConfig,
    renderCell,
    renderHead,
    onSelectEntities,
    onSearch,
    sortingChanged,
    onSelectFilter,
    resolveBannerAction,
    resolveAlign,
    openAssignModal,
    closeAssignModal,
    onFilterEntity,
    onPageChange,
    onSearchBanner,
    sortingChanged,
    resolveBannerStatusLabel,

    modalPopup,
    onOpenExportModal,
    closeReportModal,
    formModal
  };
}
